// src/components/Auth/LoginForm.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authRateLimiter } from '../../utils/rateLimiter';

const LoginForm = ({ onSuccess, onRegisterClick, onForgotPasswordClick }) => {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Check if login attempts should be rate limited
      if (!authRateLimiter.check(email, 'login')) {
        const lockoutTimeMs = authRateLimiter.getLockoutTime(email, 'login');
        const lockoutMinutes = Math.ceil(lockoutTimeMs / 60000);
        
        throw new Error(
          `Too many login attempts. Please try again in ${lockoutMinutes} minute${lockoutMinutes > 1 ? 's' : ''}.`
        );
      }

      const { error } = await signIn(email, password);
      
      if (error) throw error;
      
      // Clear rate limiting on successful login
      authRateLimiter.reset(email, 'login');
      
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }, [email, password, signIn, onSuccess]);

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  const handleForgotPasswordClick = useCallback(() => {
    onForgotPasswordClick();
  }, [onForgotPasswordClick]);

  const handleRegisterClick = useCallback(() => {
    onRegisterClick();
  }, [onRegisterClick]);

  const loadingContent = useMemo(() => {
    if (!loading) return 'Sign In';
    
    return (
      <div className="flex items-center">
        <svg className="animate-spin mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Signing In...</span>
      </div>
    );
  }, [loading]);

  return (
    <div className="px-8 py-10">
      <h2 className="text-2xl font-bold text-sws-navy mb-2">Welcome Back</h2>
      <p className="text-sws-slate mb-6">Enter your credentials to access your account</p>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p>{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-sws-slate" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
            </div>
            <input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-colors"
              placeholder="you@example.com"
              required
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-sws-slate" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-colors"
              placeholder="••••••••"
              required
            />
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-sws-purple focus:ring-sws-purple border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-sws-slate">
              Remember me
            </label>
          </div>

          <button
            type="button"
            onClick={handleForgotPasswordClick}
            className="text-sm font-medium text-sws-purple hover:text-sws-navy transition-colors"
          >
            Forgot password?
          </button>
        </div>
        
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-sws-navy hover:bg-sws-slate focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sws-purple transition-colors"
          >
            {loadingContent}
          </button>
        </div>
      </form>
      
      <div className="mt-8 text-center">
        <p className="text-sm text-sws-slate">
          Don't have an account?{' '}
          <button
            onClick={handleRegisterClick}
            className="font-medium text-sws-purple hover:text-sws-navy transition-colors"
          >
            Create an account
          </button>
        </p>
      </div>
    </div>
  );
};

export default React.memo(LoginForm);